import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  DynamicTableColDef,
  FrkBreakpoint,
  ResponsiveService,
} from '@frk/eds-components';
import { BaseTableResponsiveComponent } from '@shared/table/base-table-responsive.component';
import {
  ConfigurationId,
  DistributionLatestConfig,
  FundId,
  ProductType,
} from '@types';
import { TranslateService } from '@shared/translate/translate.service';
import { DistributionService } from '@products/pricing-distribution/services/distribution.service';
import { NotificationTypes } from '@accounts/accounts-alerts/accounts-alerts.interface';
import { UsCapitalGainsEstimatesService } from '@products/tax/services/us-capital-gains-estimates.service';
import { CapGainDistribution } from '@products/models/capgain-distribution';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { COLDEF } from 'src/app/ft-components/interactive-content/ucits-etf-data-table/types/model/indexes';
@Component({
  selector: 'ft-distribution-latest',
  templateUrl: './distribution-latest.component.html',
  styleUrls: ['./distribution-latest.component.scss'],
})
export class DistributionLatestComponent
  extends BaseTableResponsiveComponent
  implements OnInit, OnDestroy {
  @Input() latestConfig: DistributionLatestConfig;
  @Input() fundId: FundId;
  @Input() isSiteIntl: boolean;
  @Input() productType: ConfigurationId;
  @Input() latestDistributions: DistributionLatestConfig[];
  @Input() displayCapGainRecords: boolean;
  isSgHk: false;
  tooltips: { [name: string]: string } = {};
  showCanadaDistForEtfMfMmf: boolean;
  hideExDistDate: boolean;
  FrkBreakpoint = FrkBreakpoint;
  notificationType = NotificationTypes.ALERT;
  capGainRecords: CapGainDistribution[];
  private unsubscribe$: Subject<void> = new Subject<void>();
  colDefs: DynamicTableColDef<COLDEF>[];
  constructor(
    protected responsiveService: ResponsiveService,
    private translateService: TranslateService,
    private distributionService: DistributionService,
    private usCapitalGainsEstimatesService: UsCapitalGainsEstimatesService
  ) {
    super(responsiveService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.setTooltips();
    this.showCanadaDistForEtfMfMmf = this.distributionService.showCanadaDistForEtfMfMmf(
      this.productType as ProductType
    );
    this.hideExDistDate = this.distributionService.hideExDistDateForCountry();
    if (!this.isSiteIntl) {
      this.usCapitalGainsEstimatesService.populate();
      this.usCapitalGainsEstimatesService
        .getTotalRecords()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((records) => {
          if (records.length) {
            this.capGainRecords = records.filter(
              (rec) => rec.fundId === this.fundId
            );
          }
        });
    }
    this.colDefs = [
      {
        field: 'recordDate',
        headerName: this.translateService.instant('pds.INCM_DIST_RECORD_DATE'),
      },
      {
        field: 'exDate',
        headerName: this.translateService.instant(
          'products.capgains-table-heading-ex-date'
        ),
      },
      {
        field: 'shareClassName',
        headerName: this.translateService.instant(
          'products.capgains-table-heading-share-class'
        ),
      },
      {
        field: 'longTermCapitalGainPercent',
        headerName: this.translateService.instant(
          'products.capgains-table-heading-long-term-cap-gains-perc'
        ),
      },
      {
        field: 'shortTermCapitalGainPercent',
        headerName: this.translateService.instant(
          'products.capgains-table-heading-short-term-cap-gains-perc'
        ),
      },
      {
        field: 'totalGainNav',
        headerName: this.translateService.instant(
          'products.capgains-table-heading-total-percent'
        ),
      },
      {
        field: 'longTerm',
        headerName: this.translateService.instant('pds.CAPGAIN_AMT_LONG_TERM'),
      },
      {
        field: 'shortTerm',
        headerName: this.translateService.instant('pds.CAPGAIN_AMT_SHRT_TERM'),
      },
      {
        field: 'navOfValue',
        headerName: this.translateService.instant(
          'products.capgains-table-heading-nav-as-of'
        ),
      },
    ];
  }

  private setTooltips() {
    this.tooltips[
      'products.distributable-income-column-header'
    ] = this.translateService.tooltip(
      'products.distributable-income-column-header'
    );
    this.tooltips[
      'products.dividend-income-column-header'
    ] = this.translateService.tooltip('products.dividend-income-column-header');
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
